import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, Tag, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function ListingCategoriesManager({ onChanged }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newLabel, setNewLabel] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/listings/categories");
            const keys = r.data?.categories || [];
            const labels = r.data?.labels || {};
            setItems(keys.map((k) => ({ key: k, label: labels[k] || k })));
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const updateLabel = (idx, val) =>
        setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, label: val } : it)));

    const remove = async (idx) => {
        const it = items[idx];
        if (!it) return;
        if (it.key) {
            // For existing keys, try the delete endpoint to get a precise error if in use
            if (!window.confirm(`Usunąć kategorię „${it.label}”? Możliwe tylko, gdy nie ma aktywnych ogłoszeń w tej kategorii.`)) return;
            try {
                const r = await api.delete(`/listings/categories/${encodeURIComponent(it.key)}`);
                const remaining = (r.data?.items || []).map((x) => ({ key: x.key, label: x.label }));
                setItems(remaining);
                toast.success("Kategoria usunięta.");
                onChanged?.();
            } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail));
            }
        } else {
            // newly added (unsaved) — just drop it
            setItems((arr) => arr.filter((_, i) => i !== idx));
        }
    };

    const add = () => {
        const label = newLabel.trim();
        if (!label) return;
        if (items.some((it) => it.label.toLowerCase() === label.toLowerCase())) {
            toast.error("Taka kategoria już istnieje.");
            return;
        }
        setItems((arr) => [...arr, { key: "", label }]);
        setNewLabel("");
    };

    const save = async () => {
        if (items.length === 0) {
            toast.error("Wymagana jest przynajmniej jedna kategoria.");
            return;
        }
        setSaving(true);
        try {
            const r = await api.put("/listings/categories", {
                items: items.map((it) => ({ key: it.key || undefined, label: it.label.trim() })),
            });
            setItems((r.data?.items || []).map((x) => ({ key: x.key, label: x.label })));
            toast.success("Kategorie zapisane.");
            onChanged?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    if (loading) return <p className="text-gray-500 text-sm">Wczytywanie kategorii...</p>;

    return (
        <div data-testid="listing-categories-manager">
            <div className="flex items-start gap-2 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>
                    Kategorii używanej przez aktywne ogłoszenia nie można usunąć — najpierw usuń lub przenieś ogłoszenia.
                    Klucz techniczny generowany jest automatycznie z nazwy.
                </span>
            </div>

            <div className="space-y-2" data-testid="listing-categories-list">
                {items.map((it, idx) => (
                    <div key={`${it.key || "new"}-${idx}`} className="flex items-center gap-2" data-testid={`listing-category-row-${it.key || idx}`}>
                        <Tag size={14} className="text-[#D4A537] shrink-0" />
                        <Input
                            value={it.label}
                            onChange={(e) => updateLabel(idx, e.target.value)}
                            placeholder="Nazwa kategorii"
                            data-testid={`listing-category-label-${idx}`}
                            className="flex-1"
                        />
                        {it.key && <span className="text-[10px] uppercase tracking-wider text-gray-400 hidden sm:inline">{it.key}</span>}
                        <Button
                            type="button"
                            onClick={() => remove(idx)}
                            variant="outline"
                            size="icon"
                            className="rounded-full text-red-600 hover:bg-red-50 border-red-200"
                            data-testid={`listing-category-delete-${it.key || idx}`}
                            title="Usuń kategorię"
                        >
                            <Trash2 size={14} />
                        </Button>
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-5">
                <Input
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                    placeholder='Dodaj nową kategorię (np. „Wynajem mieszkania")'
                    data-testid="listing-category-new-label"
                    className="flex-1 min-w-[200px]"
                />
                <Button type="button" onClick={add} variant="outline" className="rounded-full" data-testid="listing-category-add">
                    <Plus size={14} className="mr-1.5" /> Dodaj
                </Button>
                <Button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white"
                    data-testid="listing-categories-save"
                >
                    <Save size={14} className="mr-1.5" /> {saving ? "Zapisywanie..." : "Zapisz kolejność i nazwy"}
                </Button>
            </div>
        </div>
    );
}
